import { useQuery } from "@tanstack/react-query";
import { Card, Spin, Typography, Tag, Space, Image as AntImage, Row, Col, Divider } from "antd";
import { BookOutlined, VideoCameraOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { imagesApi, worksApi } from "../services/api";

const { Text, Title, Paragraph } = Typography;

export function WorkInfoCard({ workId }: { workId?: string }) {
    const { data: work, isLoading } = useQuery({
        queryKey: ["work", workId],
        queryFn: () => worksApi.get(workId!),
        enabled: !!workId,
    });

    if (!workId) return null;

    if (isLoading) {
        return (
            <Card size="small" style={{ marginBottom: 24, textAlign: "center", padding: 24 }}>
                <Spin />
            </Card>
        );
    }

    if (!work) return null;

    return (
        <Card size="small" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 16]}>
                <Col flex="100px">
                    {work.cover_image_id ? (
                        <AntImage
                            src={imagesApi.getFileUrl(work.cover_image_id)}
                            alt={work.title}
                            width={100}
                            style={{
                                objectFit: "cover",
                                borderRadius: 4,
                                aspectRatio: "2/3",
                            }}
                        />
                    ) : (
                        <div
                            style={{
                                width: 100,
                                aspectRatio: "2/3",
                                background: "#f5f5f5",
                                borderRadius: 4,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 32,
                            }}
                        >
                            {work.type === "book" ? (
                                <BookOutlined style={{ color: "#d9d9d9" }} />
                            ) : work.type === "movie" ? (
                                <VideoCameraOutlined style={{ color: "#d9d9d9" }} />
                            ) : (
                                <PlayCircleOutlined style={{ color: "#d9d9d9" }} />
                            )}
                        </div>
                    )}
                </Col>
                <Col flex="auto" style={{ overflow: "hidden" }}>
                    <Title level={4} style={{ margin: "0 0 8px 0", wordBreak: "break-word" }}>
                        {work.title}
                        {work.release_year ? ` (${work.release_year})` : ""}
                    </Title>

                    {work.original_title && work.original_title !== work.title && (
                        <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                            Tytuł oryg.: {work.original_title}
                        </Text>
                    )}

                    <Space size={[0, 8]} wrap style={{ marginBottom: 8 }}>
                        {work.type === "book" && (
                            <Tag color="green" icon={<BookOutlined />}>Książka</Tag>
                        )}
                        {work.type === "movie" && (
                            <Tag color="blue" icon={<VideoCameraOutlined />}>Film</Tag>
                        )}
                        {work.type === "series" && (
                            <Tag color="orange" icon={<PlayCircleOutlined />}>Serial</Tag>
                        )}

                        {work.genres?.map((g) => (
                            <Tag key={g.id}>{g.name}</Tag>
                        ))}
                        {work.book_details?.literary_genres?.map((lg) => (
                            <Tag color="purple" key={lg.id}>{lg.name}</Tag>
                        ))}
                        {work.tags?.map((t) => (
                            <Tag color="default" key={t.id}>#{t.name}</Tag>
                        ))}
                    </Space>

                    <Space split={<Divider type="vertical" />} style={{ display: "flex", flexWrap: "wrap", marginBottom: 8 }}>
                        {work.type === "book" && work.book_details?.authors && work.book_details.authors.length > 0 && (
                            <Text>
                                Autor: <strong>{work.book_details.authors.map(a => a.name).join(", ")}</strong>
                            </Text>
                        )}
                        {work.type === "book" && work.book_details?.page_count && (
                            <Text>Stron: <strong>{work.book_details.page_count}</strong></Text>
                        )}
                        {work.type === "movie" && work.movie_details?.director && (
                            <Text>Reżyser: <strong>{work.movie_details.director.name}</strong></Text>
                        )}
                        {work.type === "movie" && work.movie_details?.duration_minutes && (
                            <Text>Czas trwania: <strong>{work.movie_details.duration_minutes} min</strong></Text>
                        )}
                        {work.type === "series" && work.series_details?.director && (
                            <Text>Reżyseria / Twórca: <strong>{work.series_details.director.name}</strong></Text>
                        )}
                        {work.type === "series" && work.series_details?.streaming_platform && (
                            <Text>Platforma: <strong>{work.series_details.streaming_platform.name}</strong></Text>
                        )}
                    </Space>

                    {work.description && (
                        <Paragraph
                            type="secondary"
                            ellipsis={{ rows: 3, expandable: true, symbol: "więcej" }}
                            style={{ marginBottom: 0, marginTop: 8 }}
                        >
                            {work.description}
                        </Paragraph>
                    )}
                </Col>
            </Row>
        </Card>
    );
}
